import React from "react";

import { useTranslation } from "utils";

import { CMC_STEP_4_TOGGLE_OPTIONS, CROP_ROTATION } from "models";

import { PieChart } from "components";

import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider";

const cropFamilyToColor = {
  [CROP_ROTATION.POACEAE]: "#065C45",
  [CROP_ROTATION.FABACEAE]: "#DAE7E3",
  [CROP_ROTATION.BRASSICACEAE]: "#A8DAA5",
  [CROP_ROTATION.ASTERACEAE]: "#B4CEC7",
  [CROP_ROTATION.LINACEAE]: "#66A58D",
  [CROP_ROTATION.POLYGONACEAE]: "#306E5D",
  [CROP_ROTATION.SOLANACEAE]: "#81C67F",
  [CROP_ROTATION.HYDROPHYLLACEAE]: "#B9D0FB",
  [CROP_ROTATION.AMARANTHACEAE]: "#92ABA4",
  [CROP_ROTATION.OTHER]: "#B4D1C6",
};

export const CropMixPieChart: React.FC<{ selectedToggleOption: CMC_STEP_4_TOGGLE_OPTIONS }> = ({ selectedToggleOption }) => {
  const { t } = useTranslation("cropMixComposer");
  const { weightShareDistribution, selectedCrops } = useCropMixComposerContext();

  if (!selectedCrops.length) {
    return <div>{t("noCropSelected")}</div>;
  }

  const cropFamilyGroupMap: Partial<Record<CROP_ROTATION, { crops: { name: string; value: number }[]; total: number }>> = {};

  selectedCrops.forEach((crop) => {
    const family = crop.crop.cropFamily;
    if (!cropFamilyGroupMap[family]) {
      cropFamilyGroupMap[family] = { crops: [], total: 0 };
    }

    const dist = weightShareDistribution[crop.crop.id];
    const value = selectedToggleOption === CMC_STEP_4_TOGGLE_OPTIONS.WEIGHT_SHARE ? dist?.weightShare ?? 0 : dist?.weightSeedingRate ?? 0;

    cropFamilyGroupMap[family].crops.push({ name: crop.crop.name, value });
    cropFamilyGroupMap[family].total += value;
  });

  const dataWithColors = Object.entries(cropFamilyGroupMap)
    .filter(([_, group]) => group.total > 0)
    .map(([family, group]) => ({
      label: t(`plantFamilies.${family}`),
      value: group.total,
      color: cropFamilyToColor[family as CROP_ROTATION] || cropFamilyToColor[CROP_ROTATION.OTHER],
    }))
    .sort((a, b) => b.value - a.value);

  const pieData = dataWithColors.map((item) => ({ label: item.label, value: item.value }));
  const pieColors = dataWithColors.map((item) => item.color);

  const suffix = selectedToggleOption === CMC_STEP_4_TOGGLE_OPTIONS.WEIGHT_SHARE ? "%" : " kg/ha";

  return <PieChart data={pieData} colors={pieColors} tooltipFormatter={(label, value) => `${label}: ${value}${suffix}`} />;
};
